import { useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';

interface PWAState {
  isInstalled: boolean;
  isOnline: boolean;
  hasUpdate: boolean;
  isUpdateReady: boolean;
  registration: ServiceWorkerRegistration | null;
}

interface PWAUpdateInfo {
  hasUpdate: boolean;
  isUpdateReady: boolean;
  updateApp: () => void;
}

export const usePWA = (): PWAState & PWAUpdateInfo => {
  const [state, setState] = useState<PWAState>({
    isInstalled: false,
    isOnline: navigator.onLine,
    hasUpdate: false,
    isUpdateReady: false,
    registration: null,
  });

  // Check if app is installed
  const checkInstallation = useCallback(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    
    setState(prev => ({
      ...prev,
      isInstalled: isStandalone || isIOSStandalone,
    }));
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      logger.debug('StringPak PWA: Service Worker not supported');
      return;
    }

    // In development, clear cache on page load to prevent stale content
    const isDevelopment = window.location.hostname === 'localhost' || 
                          window.location.hostname === '127.0.0.1';
    
    // Only clear cache once per session to prevent loops
    const cacheClearedKey = 'sw-cache-cleared';
    if (isDevelopment && 'caches' in window && !sessionStorage.getItem(cacheClearedKey)) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            logger.debug('StringPak PWA: Clearing cache in development:', cacheName);
            return caches.delete(cacheName);
          })
        );
        sessionStorage.setItem(cacheClearedKey, 'true');
        logger.info('StringPak PWA: All caches cleared in development mode');
      } catch (error) {
        logger.warn('StringPak PWA: Error clearing caches:', error);
      }
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        updateViaCache: 'none' // Always fetch the service worker script from the network
      });
      logger.info('StringPak PWA: Service Worker registered successfully:', registration);

      setState(prev => ({
        ...prev,
        registration,
      }));

      // Listen for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setState(prev => ({
                ...prev,
                hasUpdate: true,
                isUpdateReady: true,
              }));
            }
          });
        }
      });

      // Handle controller change
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        logger.info('StringPak PWA: New service worker activated');
        setState(prev => ({
          ...prev,
          hasUpdate: false,
          isUpdateReady: false,
        }));
        // Don't auto-reload in development to prevent loops
        // User can manually refresh if needed
      });

      // In development, unregister and re-register service worker on each load to ensure fresh code
      if (isDevelopment && registration.active) {
        // Send message to clear cache
        registration.active.postMessage({ type: 'CLEAR_CACHE' });
      }

    } catch (error) {
      logger.error('StringPak PWA: Service Worker registration failed:', error);
    }
  }, []);

  // Update app
  const updateApp = useCallback(() => {
    if (state.registration && state.registration.waiting) {
      // Send message to service worker to skip waiting
      state.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Reload the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, [state.registration]);

  // Handle online/offline events
  const handleOnlineStatus = useCallback(() => {
    setState(prev => ({
      ...prev,
      isOnline: navigator.onLine,
    }));
  }, []);

  // Initialize PWA
  useEffect(() => {
    checkInstallation();
    registerServiceWorker();

    // Listen for online/offline events
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    // Listen for app installed event
    const handleAppInstalled = () => {
      logger.info('StringPak PWA: App installed');
      setState(prev => ({
        ...prev,
        isInstalled: true,
      }));
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [checkInstallation, registerServiceWorker, handleOnlineStatus]);

  return {
    ...state,
    updateApp,
  };
};

// Hook for PWA install prompt
export const usePWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    const handleAppInstalled = () => {
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installApp = async () => {
    if (!deferredPrompt) return false;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        logger.info('StringPak PWA: User accepted the install prompt');
        setShowInstallPrompt(false);
        setDeferredPrompt(null);
        return true;
      } else {
        logger.debug('StringPak PWA: User dismissed the install prompt');
        return false;
      }
    } catch (error) {
      logger.error('StringPak PWA: Error during install:', error);
      return false;
    }
  };

  const dismissPrompt = () => {
    setShowInstallPrompt(false);
    setDeferredPrompt(null);
  };

  return {
    showInstallPrompt,
    installApp,
    dismissPrompt,
    canInstall: !!deferredPrompt,
  };
};

// Hook for offline functionality
export const useOffline = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineData, setOfflineData] = useState<any>(null);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const saveOfflineData = useCallback((key: string, data: any) => {
    try {
      localStorage.setItem(`stringpak-offline-${key}`, JSON.stringify(data));
    } catch (error) {
      logger.error('StringPak PWA: Error saving offline data:', error);
    }
  }, []);

  const getOfflineData = useCallback((key: string) => {
    try {
      const data = localStorage.getItem(`stringpak-offline-${key}`);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      logger.error('StringPak PWA: Error getting offline data:', error);
      return null;
    }
  }, []);

  return {
    isOffline,
    offlineData,
    saveOfflineData,
    getOfflineData,
  };
};
